import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

// Definição de permissões do sistema
export interface PermissionConfig {
  // Módulos disponíveis
  modules: {
    [key: string]: ModulePermission;
  };
}

export interface ModulePermission {
  id: string;
  name: string;
  description: string;
  icon?: string;
  category: 'crm' | 'communication' | 'automation' | 'intelligence' | 'reports' | 'settings';
  requiredRole: 'admin' | 'manager' | 'agent' | 'viewer';
  defaultEnabled: boolean;
}

// Permissões que cada role tem acesso
export interface RolePermissions {
  role: string;
  permissions: string[]; // IDs dos módulos permitidos
  canViewAllData: boolean; // Se pode ver todos os dados ou apenas os seus
  canManageUsers: boolean;
  canManageSettings: boolean;
}

interface PermissionsContextData {
  // Permissões do usuário atual
  userPermissions: string[];
  canViewAllData: boolean;
  canManageUsers: boolean;
  canManageSettings: boolean;
  
  // Verificações de permissão
  hasPermission: (moduleId: string) => boolean;
  hasAnyPermission: (moduleIds: string[]) => boolean;
  hasAllPermissions: (moduleIds: string[]) => boolean;
  
  // Verificações de role
  isAdmin: boolean;
  isManager: boolean;
  isAgent: boolean;
  isViewer: boolean;
  
  // Configuração do sistema (para admin)
  systemPermissions: PermissionConfig;
  roleConfigs: RolePermissions[];
  updateRolePermissions: (role: string, permissions: string[]) => Promise<void>;
  
  // Loading
  loading: boolean;
}

const PermissionsContext = createContext<PermissionsContextData>({} as PermissionsContextData);

// Configuração padrão do sistema
const DEFAULT_PERMISSIONS: PermissionConfig = {
  modules: {
    // CRM
    'deals': {
      id: 'deals',
      name: 'Negociações',
      description: 'Gerenciar negociações e pipelines',
      category: 'crm',
      requiredRole: 'agent',
      defaultEnabled: true,
    },
    'leads': {
      id: 'leads',
      name: 'Leads',
      description: 'Gerenciar leads e prospects',
      category: 'crm',
      requiredRole: 'agent',
      defaultEnabled: true,
    },
    'contacts': {
      id: 'contacts',
      name: 'Contatos',
      description: 'Gerenciar contatos',
      category: 'crm',
      requiredRole: 'agent',
      defaultEnabled: true,
    },
    'activities': {
      id: 'activities',
      name: 'Tarefas',
      description: 'Gerenciar tarefas e atividades',
      category: 'crm',
      requiredRole: 'agent',
      defaultEnabled: true,
    },
    
    // Comunicação
    'messaging': {
      id: 'messaging',
      name: 'Mensagens',
      description: 'Gerenciar mensagens e conversas',
      category: 'communication',
      requiredRole: 'agent',
      defaultEnabled: true,
    },
    'templates': {
      id: 'templates',
      name: 'Templates',
      description: 'Gerenciar templates de mensagens',
      category: 'communication',
      requiredRole: 'agent',
      defaultEnabled: true,
    },
    
    // Automação
    'automations': {
      id: 'automations',
      name: 'Workflows',
      description: 'Gerenciar automações e workflows',
      category: 'automation',
      requiredRole: 'manager',
      defaultEnabled: false,
    },
    'pipelines': {
      id: 'pipelines',
      name: 'Pipelines',
      description: 'Gerenciar pipelines de vendas',
      category: 'automation',
      requiredRole: 'manager',
      defaultEnabled: false,
    },
    
    // Inteligência Artificial
    'ai-agents': {
      id: 'ai-agents',
      name: 'Agentes de IA',
      description: 'Gerenciar agentes de inteligência artificial',
      category: 'intelligence',
      requiredRole: 'admin',
      defaultEnabled: false,
    },
    'ai-settings': {
      id: 'ai-settings',
      name: 'Configurações de IA',
      description: 'Configurar parâmetros de IA',
      category: 'intelligence',
      requiredRole: 'admin',
      defaultEnabled: false,
    },
    
    // Relatórios
    'analytics': {
      id: 'analytics',
      name: 'Analytics',
      description: 'Ver análises e métricas',
      category: 'reports',
      requiredRole: 'manager',
      defaultEnabled: false,
    },
    'reports': {
      id: 'reports',
      name: 'Relatórios',
      description: 'Gerar relatórios personalizados',
      category: 'reports',
      requiredRole: 'manager',
      defaultEnabled: false,
    },
    'performance': {
      id: 'performance',
      name: 'Performance',
      description: 'Ver métricas de performance',
      category: 'reports',
      requiredRole: 'manager',
      defaultEnabled: false,
    },
    
    // Configurações
    'settings': {
      id: 'settings',
      name: 'Configurações',
      description: 'Configurações gerais do sistema',
      category: 'settings',
      requiredRole: 'admin',
      defaultEnabled: false,
    },
    'company-users': {
      id: 'company-users',
      name: 'Usuários',
      description: 'Gerenciar usuários da empresa',
      category: 'settings',
      requiredRole: 'admin',
      defaultEnabled: false,
    },
    'integrations': {
      id: 'integrations',
      name: 'Integrações',
      description: 'Gerenciar integrações com outros sistemas',
      category: 'settings',
      requiredRole: 'admin',
      defaultEnabled: false,
    },
    'webhooks': {
      id: 'webhooks',
      name: 'Webhooks',
      description: 'Configurar webhooks',
      category: 'settings',
      requiredRole: 'admin',
      defaultEnabled: false,
    },
    'custom-fields': {
      id: 'custom-fields',
      name: 'Campos Customizados',
      description: 'Criar campos customizados',
      category: 'settings',
      requiredRole: 'admin',
      defaultEnabled: false,
    },
    'teams': {
      id: 'teams',
      name: 'Equipes',
      description: 'Gerenciar equipes de trabalho',
      category: 'settings',
      requiredRole: 'manager',
      defaultEnabled: false,
    },
  },
};

// Configuração padrão de roles
const DEFAULT_ROLE_CONFIGS: RolePermissions[] = [
  {
    role: 'admin',
    permissions: Object.keys(DEFAULT_PERMISSIONS.modules),
    canViewAllData: true,
    canManageUsers: true,
    canManageSettings: true,
  },
  {
    role: 'manager',
    permissions: [
      'deals', 'leads', 'contacts', 'activities',
      'messaging', 'templates',
      'automations', 'pipelines',
      'analytics', 'reports', 'performance',
      'teams',
    ],
    canViewAllData: true,
    canManageUsers: false,
    canManageSettings: false,
  },
  {
    role: 'agent',
    permissions: [
      'deals', 'leads', 'contacts', 'activities',
      'messaging', 'templates',
    ],
    canViewAllData: false,
    canManageUsers: false,
    canManageSettings: false,
  },
  {
    role: 'viewer',
    permissions: [
      'deals', 'leads', 'contacts', 'activities',
    ],
    canViewAllData: false,
    canManageUsers: false,
    canManageSettings: false,
  },
];

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [systemPermissions, setSystemPermissions] = useState<PermissionConfig>(DEFAULT_PERMISSIONS);
  const [roleConfigs, setRoleConfigs] = useState<RolePermissions[]>(DEFAULT_ROLE_CONFIGS);
  
  // Calcular permissões do usuário atual
  const userRoleConfig = roleConfigs.find(config => config.role === user?.role) || roleConfigs[2]; // Default to agent
  
  const userPermissions = userRoleConfig.permissions;
  const canViewAllData = userRoleConfig.canViewAllData;
  const canManageUsers = userRoleConfig.canManageUsers;
  const canManageSettings = userRoleConfig.canManageSettings;
  
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const isAgent = user?.role === 'agent';
  const isViewer = user?.role === 'viewer';
  
  // Carregar configurações de permissões do backend (apenas para admin)
  useEffect(() => {
    const loadPermissions = async () => {
      if (isAdmin) {
        try {
          // TODO: Carregar do backend quando a API estiver pronta
          // const response = await api.get('/auth/permissions/');
          // setSystemPermissions(response.data.system);
          // setRoleConfigs(response.data.roles);
        } catch (error) {
          console.error('Erro ao carregar permissões:', error);
        }
      }
      setLoading(false);
    };
    
    loadPermissions();
  }, [isAdmin]);
  
  // Funções de verificação de permissão
  const hasPermission = (moduleId: string): boolean => {
    return userPermissions.includes(moduleId);
  };
  
  const hasAnyPermission = (moduleIds: string[]): boolean => {
    return moduleIds.some(id => userPermissions.includes(id));
  };
  
  const hasAllPermissions = (moduleIds: string[]): boolean => {
    return moduleIds.every(id => userPermissions.includes(id));
  };
  
  // Atualizar permissões de um role (apenas admin)
  const updateRolePermissions = async (role: string, permissions: string[]) => {
    if (!isAdmin) {
      throw new Error('Apenas administradores podem alterar permissões');
    }
    
    try {
      // TODO: Enviar para backend quando a API estiver pronta
      // await api.put(`/auth/permissions/roles/${role}/`, { permissions });
      
      // Atualizar estado local
      setRoleConfigs(prev => 
        prev.map(config => 
          config.role === role 
            ? { ...config, permissions }
            : config
        )
      );
    } catch (error) {
      console.error('Erro ao atualizar permissões:', error);
      throw error;
    }
  };
  
  return (
    <PermissionsContext.Provider
      value={{
        userPermissions,
        canViewAllData,
        canManageUsers,
        canManageSettings,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        isAdmin,
        isManager,
        isAgent,
        isViewer,
        systemPermissions,
        roleConfigs,
        updateRolePermissions,
        loading,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context;
};

export default PermissionsContext;